/**
 * Punnett-square arithmetic for crosses of one to three unlinked genes, one of which
 * may be sex-linked.
 *
 * Model: every gene has two alleles, written as the upper- and lower-case form of one
 * letter (A / a). The genes assort independently (no linkage), each gamete carries one
 * allele per gene, and the square lists the distinct gametes of each parent — so every
 * box has the same probability, 1 / (rows × columns). A sex-linked gene sits on the X
 * (XX female, XY male) or on the Z (ZW female, ZZ male); the heterogametic parent
 * carries one allele and passes either it or the Y/W chromosome, which has no allele.
 */

export type Dominance = "complete" | "incomplete" | "codominant";
export type SexSystem = "xy" | "zw";
export type Sex = "female" | "male";

export interface GeneInput {
  /** One letter; the dominant allele is its upper-case form. */
  symbol: string;
  dominance: Dominance;
  /** Copies of the upper-case allele in parent 1 (0–2; 0–1 when hemizygous). */
  parent1: number;
  /** The same for parent 2. */
  parent2: number;
}

export interface CrossInput {
  genes: readonly GeneInput[];
  /** Gene 1 is on the sex chromosomes. Parent 1 is then the female, parent 2 the male. */
  sexLinked: boolean;
  system: SexSystem;
}

/** One allele (or the allele-free Y/W) carried by a gamete or an offspring. */
export interface AlleleToken {
  /** "X", "Y", "Z" or "W" for a sex-linked gene; null for an autosomal one. */
  chrom: string | null;
  /** The allele letter, or null on a Y or W. */
  allele: string | null;
  dominant: boolean;
}

/** A piece of a label: `base` and, when present, a superscript (the X^A notation). */
export interface LabelPart {
  base: string;
  sup?: string;
}

export interface OffspringGene {
  alleles: [AlleleToken, AlleleToken];
  /** 0 = homozygous for the dominant allele, 1 = heterozygous, 2 = homozygous recessive. */
  genotypeClass: 0 | 1 | 2;
  /** 0 and 1 under complete dominance (dominant, recessive); 0–2 otherwise. */
  phenotypeClass: 0 | 1 | 2;
}

export interface Category {
  /** Stable identity, also the plain-text label. */
  key: string;
  count: number;
  /** Order key: compare lexicographically to list dominant forms first. */
  order: number[];
  /** Sex of the offspring when a gene is sex-linked, otherwise null. */
  sex: Sex | null;
}

export interface GenotypeCategory extends Category {
  /** One label per gene, drawn as parts so superscripts survive. */
  genes: LabelPart[][];
  genotypeClasses: number[];
}

export interface PhenotypeCategory extends Category {
  phenotypeClasses: number[];
}

export interface Gamete {
  tokens: AlleleToken[];
  key: string;
}

export interface CrossResult {
  rows: Gamete[];
  cols: Gamete[];
  /** For each box, the index into `genotypes` and `phenotypes`. */
  cells: { genotype: number; phenotype: number }[][];
  genotypes: GenotypeCategory[];
  phenotypes: PhenotypeCategory[];
  total: number;
}

const HOMOGAMETIC_CHROM: Record<SexSystem, string> = { xy: "X", zw: "Z" };
const HETEROGAMETIC_CHROM: Record<SexSystem, string> = { xy: "Y", zw: "W" };

export function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    [x, y] = [y, x % y];
  }
  return x;
}

export function gcdOf(values: readonly number[]): number {
  return values.reduce((acc, value) => gcd(acc, value), 0);
}

function upper(symbol: string): string {
  return symbol.toUpperCase();
}

function lower(symbol: string): string {
  return symbol.toLowerCase();
}

function alleleToken(
  symbol: string,
  dominant: boolean,
  chrom: string | null
): AlleleToken {
  return {
    chrom,
    allele: dominant ? upper(symbol) : lower(symbol),
    dominant,
  };
}

function tokenParts(token: AlleleToken): LabelPart {
  if (token.chrom === null) {
    return { base: token.allele ?? "" };
  }
  return token.allele === null
    ? { base: token.chrom }
    : { base: token.chrom, sup: token.allele };
}

export function partsText(parts: readonly LabelPart[]): string {
  return parts
    .map((part) => (part.sup ? `${part.base}^${part.sup}` : part.base))
    .join("");
}

/**
 * Gametes for one parent. `copies` is the number of dominant alleles the parent carries
 * for this gene; `hemizygous` means a single allele on the sex chromosome.
 */
function gametesForGene(
  symbol: string,
  copies: number,
  sex: { chrom: string; other: string } | null,
  hemizygous: boolean
): AlleleToken[] {
  const chrom = sex ? sex.chrom : null;
  if (hemizygous && sex) {
    return [
      alleleToken(symbol, copies >= 1, chrom),
      { chrom: sex.other, allele: null, dominant: false },
    ];
  }
  if (copies >= 2) {
    return [alleleToken(symbol, true, chrom)];
  }
  if (copies <= 0) {
    return [alleleToken(symbol, false, chrom)];
  }
  return [alleleToken(symbol, true, chrom), alleleToken(symbol, false, chrom)];
}

function tokensKey(tokens: readonly AlleleToken[]): string {
  return tokens
    .map((token) => `${token.chrom ?? ""}${token.allele ?? ""}`)
    .join("|");
}

function cartesian(options: readonly AlleleToken[][]): Gamete[] {
  let combos: AlleleToken[][] = [[]];
  for (const choices of options) {
    combos = combos.flatMap((combo) =>
      choices.map((choice) => [...combo, choice])
    );
  }
  return combos.map((tokens) => ({ tokens, key: tokensKey(tokens) }));
}

/** The distinct gametes of parent `which` (1 or 2), in a stable dominant-first order. */
export function parentGametes(input: CrossInput, which: 1 | 2): Gamete[] {
  const { genes, sexLinked, system } = input;
  const homogameticParent = system === "xy" ? 1 : 2;
  const options = genes.map((gene, index) => {
    const copies = which === 1 ? gene.parent1 : gene.parent2;
    if (index === 0 && sexLinked) {
      return gametesForGene(
        gene.symbol,
        copies,
        {
          chrom: HOMOGAMETIC_CHROM[system],
          other: HETEROGAMETIC_CHROM[system],
        },
        which !== homogameticParent
      );
    }
    return gametesForGene(gene.symbol, copies, null, false);
  });
  return cartesian(options);
}

/** Dominant allele first; for a sex-linked pair, the Y or W last. */
function orderPair(a: AlleleToken, b: AlleleToken): [AlleleToken, AlleleToken] {
  const rank = (token: AlleleToken): number => {
    if (token.allele === null) {
      return 2;
    }
    return token.dominant ? 0 : 1;
  };
  return rank(a) <= rank(b) ? [a, b] : [b, a];
}

function offspringGene(
  a: AlleleToken,
  b: AlleleToken,
  dominance: Dominance
): OffspringGene {
  const alleles = orderPair(a, b);
  const carried = alleles.filter((token) => token.allele !== null);
  const dominantCount = carried.filter((token) => token.dominant).length;
  let genotypeClass: 0 | 1 | 2 = 1;
  if (dominantCount === carried.length) {
    genotypeClass = 0;
  } else if (dominantCount === 0) {
    genotypeClass = 2;
  }
  let phenotypeClass: 0 | 1 | 2 = genotypeClass;
  if (dominance === "complete") {
    phenotypeClass = genotypeClass === 2 ? 1 : 0;
  }
  return { alleles, genotypeClass, phenotypeClass };
}

function geneParts(gene: OffspringGene): LabelPart[] {
  return gene.alleles.map(tokenParts);
}

function offspringSex(gene: OffspringGene, system: SexSystem): Sex {
  const heterogametic = HETEROGAMETIC_CHROM[system];
  const isHetero = gene.alleles.some((token) => token.chrom === heterogametic);
  if (system === "xy") {
    return isHetero ? "male" : "female";
  }
  return isHetero ? "female" : "male";
}

function sexRank(sex: Sex): number {
  return sex === "female" ? 0 : 1;
}

interface Zygote {
  genes: OffspringGene[];
  sex: Sex | null;
}

function fertilize(input: CrossInput, first: Gamete, second: Gamete): Zygote {
  const genes = input.genes.map((gene, index) =>
    offspringGene(first.tokens[index], second.tokens[index], gene.dominance)
  );
  const sex =
    input.sexLinked && genes[0] ? offspringSex(genes[0], input.system) : null;
  return { genes, sex };
}

function genotypeOrder(zygote: Zygote): number[] {
  return zygote.genes.map((gene, index) => {
    if (index === 0 && zygote.sex !== null) {
      return sexRank(zygote.sex) * 3 + gene.genotypeClass;
    }
    return gene.genotypeClass;
  });
}

function genotypeKey(zygote: Zygote): string {
  return zygote.genes.map((gene) => partsText(geneParts(gene))).join(" ");
}

function phenotypeKey(zygote: Zygote): string {
  const classes = zygote.genes.map((gene) => gene.phenotypeClass).join("");
  return `${classes}:${zygote.sex ?? ""}`;
}

function compareOrder(a: readonly number[], b: readonly number[]): number {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return a[i] - b[i];
    }
  }
  return 0;
}

/** Run a cross. Returns null when the input has no genes. */
export function runCross(input: CrossInput): CrossResult | null {
  if (input.genes.length === 0) {
    return null;
  }
  const rows = parentGametes(input, 1);
  const cols = parentGametes(input, 2);
  const genotypeMap = new Map<string, GenotypeCategory>();
  const phenotypeMap = new Map<string, PhenotypeCategory>();
  const placements: { gKey: string; pKey: string }[][] = [];

  for (const row of rows) {
    const placed: { gKey: string; pKey: string }[] = [];
    for (const col of cols) {
      const zygote = fertilize(input, row, col);
      const gKey = genotypeKey(zygote);
      const pKey = phenotypeKey(zygote);
      const genotype = genotypeMap.get(gKey);
      if (genotype) {
        genotype.count += 1;
      } else {
        genotypeMap.set(gKey, {
          key: gKey,
          count: 1,
          order: genotypeOrder(zygote),
          sex: zygote.sex,
          genes: zygote.genes.map(geneParts),
          genotypeClasses: zygote.genes.map((gene) => gene.genotypeClass),
        });
      }
      const phenotype = phenotypeMap.get(pKey);
      if (phenotype) {
        phenotype.count += 1;
      } else {
        const phenotypeClasses = zygote.genes.map(
          (gene) => gene.phenotypeClass
        );
        phenotypeMap.set(pKey, {
          key: pKey,
          count: 1,
          order: [...phenotypeClasses, zygote.sex ? sexRank(zygote.sex) : 0],
          sex: zygote.sex,
          phenotypeClasses,
        });
      }
      placed.push({ gKey, pKey });
    }
    placements.push(placed);
  }

  const genotypes = [...genotypeMap.values()].sort((a, b) =>
    compareOrder(a.order, b.order)
  );
  const phenotypes = [...phenotypeMap.values()].sort((a, b) =>
    compareOrder(a.order, b.order)
  );
  const genotypeIndex = new Map(genotypes.map((g, i) => [g.key, i]));
  const phenotypeIndex = new Map(phenotypes.map((p, i) => [p.key, i]));
  const cells = placements.map((row) =>
    row.map((cell) => ({
      genotype: genotypeIndex.get(cell.gKey) ?? 0,
      phenotype: phenotypeIndex.get(cell.pKey) ?? 0,
    }))
  );
  return {
    rows,
    cols,
    cells,
    genotypes,
    phenotypes,
    total: rows.length * cols.length,
  };
}

/** Counts reduced by their greatest common divisor: 36 : 12 : 12 : 4 becomes 9 : 3 : 3 : 1. */
export function reduceRatio(counts: readonly number[]): number[] {
  const divisor = gcdOf(counts);
  return divisor === 0 ? [...counts] : counts.map((count) => count / divisor);
}

/** A reduced fraction of `total`, such as 9/16 or 1/2. */
export function reduceFraction(
  count: number,
  total: number
): { numerator: number; denominator: number } {
  const divisor = gcd(count, total) || 1;
  return { numerator: count / divisor, denominator: total / divisor };
}

/** The label of a gamete or genotype made of several genes, as plain text. */
export function geneListText(genes: readonly (readonly LabelPart[])[]): string {
  return genes.map(partsText).join(" ");
}

export function gameteParts(gamete: Gamete): LabelPart[][] {
  return gamete.tokens.map((token) => [tokenParts(token)]);
}

/** Is `symbol` a single letter that has distinct upper- and lower-case forms? */
export function isCasedLetter(symbol: string): boolean {
  return [...symbol].length === 1 && upper(symbol) !== lower(symbol);
}
